<script setup>
import { useAlert } from 'dashboard/composables';
import { usePolicy } from 'dashboard/composables/usePolicy';
import BaseSettingsHeader from '../components/BaseSettingsHeader.vue';
import SettingsLayout from '../SettingsLayout.vue';
import { computed, ref } from 'vue';
import { picoSearch } from '@scmmishra/pico-search';
import { useMapGetter } from 'dashboard/composables/store.js';
import { useStoreGetters, useStore } from 'dashboard/composables/store';
import { useI18n } from 'vue-i18n';

import Icon from 'dashboard/components-next/icon/Icon.vue';
import Button from 'dashboard/components-next/button/Button.vue';

const store = useStore();
const { t } = useI18n();
const getters = useStoreGetters();
const { checkPermissions } = usePolicy();

const canManageBranches = computed(() =>
  checkPermissions(['administrator', 'branch_manage'])
);

const loading = ref({});
const searchQuery = ref('');

const branchesList = useMapGetter('branches/getBranches');

const filteredBranchesList = computed(() => {
  const query = searchQuery.value.trim();
  if (!query) return branchesList.value;
  return picoSearch(branchesList.value, query, ['name', 'description']);
});

const uiFlags = computed(() => getters['branches/getUIFlags'].value);

const deleteBranch = async ({ id }) => {
  try {
    loading.value[id] = true;
    await store.dispatch('branches/delete', id);
    useAlert(t('TEAMS_SETTINGS.DELETE.API.SUCCESS_MESSAGE'));
  } catch (error) {
    useAlert(t('TEAMS_SETTINGS.DELETE.API.ERROR_MESSAGE'));
  } finally {
    loading.value[id] = false;
  }
};

const showDeletePopup = ref(false);
const selectedBranch = ref({});

// eslint-disable-next-line no-unused-vars
const openDelete = branch => {
  showDeletePopup.value = true;
  selectedBranch.value = branch;
};

const closeDelete = () => {
  showDeletePopup.value = false;
  selectedBranch.value = {};
};

const confirmDeletion = () => {
  deleteBranch(selectedBranch.value);
  closeDelete();
};

const deleteConfirmText = computed(
  () => `${t('TEAMS_SETTINGS.DELETE.CONFIRM.YES')} ${selectedBranch.value.name}`
);

const deleteRejectText = computed(() => t('TEAMS_SETTINGS.DELETE.CONFIRM.NO'));

const confirmDeleteTitle = computed(() =>
  t('TEAMS_SETTINGS.DELETE.CONFIRM.TITLE', {
    teamName: selectedBranch.value.name,
  })
);

const confirmPlaceHolderText = computed(() =>
  t('TEAMS_SETTINGS.DELETE.CONFIRM.PLACE_HOLDER', {
    teamName: selectedBranch.value.name,
  })
);
</script>

<template>
  <SettingsLayout
    :is-loading="uiFlags.isFetching"
    :loading-message="$t('TEAMS_SETTINGS.LOADING')"
    :no-records-found="!branchesList.length"
    :no-records-message="$t('TEAMS_SETTINGS.LIST.404')"
  >
    <template #header>
      <BaseSettingsHeader
        v-model:search-query="searchQuery"
        :title="$t('BRANCHES_SETTINGS.HEADER')"
        :description="$t('BRANCHES_SETTINGS.DESCRIPTION')"
        :search-placeholder="$t('BRANCHES_SETTINGS.SEARCH_PLACEHOLDER')"
      >
        <template v-if="branchesList?.length" #count>
          <span class="text-body-main text-n-slate-11">
            {{ $t('BRANCHES_SETTINGS.COUNT', { count: branchesList.length }) }}
          </span>
        </template>
        <template #actions />
      </BaseSettingsHeader>
    </template>
    <template #body>
      <span
        v-if="!filteredBranchesList.length && searchQuery"
        class="flex-1 flex items-center justify-center py-20 text-center text-body-main !text-base text-n-slate-11"
      >
        {{ $t('BRANCHES_SETTINGS.NO_RESULTS') }}
      </span>

      <div v-else class="divide-y divide-n-weak border-t border-n-weak">
        <div
          v-for="branch in filteredBranchesList"
          :key="branch.id"
          class="flex justify-between flex-row items-start gap-4 py-4"
        >
          <div class="flex items-start gap-4">
            <div
              class="flex items-center flex-shrink-0 size-10 justify-center rounded-xl outline outline-1 outline-n-weak -outline-offset-1"
            >
              <Icon icon="i-lucide-building-2" class="size-4 text-n-slate-11" />
            </div>
            <div class="flex flex-col items-start gap-1">
              <span class="block text-heading-3 text-n-slate-12 capitalize">
                {{ branch.name }}
              </span>
              <p class="mb-0 text-n-slate-11 text-body-main">
                {{ branch.description }}
              </p>
            </div>
          </div>
          <div class="flex justify-end gap-1">
            <router-link
              :to="{
                name: 'settings_branches_edit_teams',
                params: { branchId: branch.id },
              }"
            >
              <Button
                v-if="canManageBranches"
                v-tooltip.top="$t('BRANCHES_SETTINGS.LIST.EDIT_TEAMS')"
                icon="i-lucide-users"
                slate
                sm
              />
            </router-link>
            <router-link
              :to="{
                name: 'settings_branches_edit_members',
                params: { branchId: branch.id },
              }"
            >
              <Button
                v-if="canManageBranches"
                v-tooltip.top="$t('BRANCHES_SETTINGS.LIST.EDIT_AGENTS')"
                icon="i-lucide-user"
                slate
                sm
              />
            </router-link>
            <router-link
              :to="{
                name: 'settings_branches_edit',
                params: { branchId: branch.id },
              }"
            >
              <Button
                v-if="canManageBranches"
                v-tooltip.top="$t('BRANCHES_SETTINGS.LIST.EDIT_BRANCH')"
                icon="i-woot-settings"
                slate
                sm
              />
            </router-link>
          </div>
        </div>
      </div>
    </template>
    <woot-confirm-delete-modal
      v-if="showDeletePopup"
      v-model:show="showDeletePopup"
      :title="confirmDeleteTitle"
      :message="$t('TEAMS_SETTINGS.DELETE.CONFIRM.MESSAGE')"
      :confirm-text="deleteConfirmText"
      :reject-text="deleteRejectText"
      :confirm-value="selectedBranch.name"
      :confirm-place-holder-text="confirmPlaceHolderText"
      @on-confirm="confirmDeletion"
      @on-close="closeDelete"
    />
  </SettingsLayout>
</template>
